// VoiceContext — one place that owns Miko's voice UX so every screen can use it.
//
// Holds:
//   • enabled        — the "Miko voice" setting (persisted, default ON)
//   • micState       — shared idle | listening | thinking | speaking, so the mic
//                      button glows correctly whether the state was driven by the
//                      player tapping the mic OR by a game moment speaking a line
//   • speak(line)    — MIKO SPEAKS primitive: speaks aloud (if enabled) and drives
//                      micState to 'speaking' for the duration
//
// The mic conversation flow (listen → think → reply) lives in MicButton, which
// has store access to build live stats; it calls setMicState + speak here.

import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { speak as ttsSpeak, stopSpeaking } from './speech.js';

export type MicState = 'idle' | 'listening' | 'thinking' | 'speaking';

type VoiceApi = {
  enabled: boolean;
  setEnabled: (v: boolean) => void;
  micState: MicState;
  setMicState: (s: MicState) => void;
  /** Speak a line in Miko's voice (respects the enabled setting). Resolves when
   *  speech ends. Safe to call from any game moment. */
  speak: (text: string) => Promise<void>;
};

const Ctx = createContext<VoiceApi | null>(null);

const LS_KEY = 'bizkid-voice';
function readEnabled(): boolean {
  try {
    const v = localStorage.getItem(LS_KEY);
    return v === null ? true : v === '1'; // default ON
  } catch {
    return true;
  }
}

export function VoiceProvider({ children }: { children: React.ReactNode }) {
  const [enabled, setEnabledState] = useState<boolean>(readEnabled);
  const [micState, setMicStateRaw] = useState<MicState>('idle');
  // Guard so a game-moment speak() doesn't clobber a live listening/thinking
  // state set by the mic button.
  const micStateRef = useRef<MicState>('idle');
  const setMicState = useCallback((s: MicState) => {
    micStateRef.current = s;
    setMicStateRaw(s);
  }, []);

  const setEnabled = useCallback((v: boolean) => {
    setEnabledState(v);
    try {
      localStorage.setItem(LS_KEY, v ? '1' : '0');
    } catch {
      /* ignore */
    }
    if (!v) stopSpeaking();
  }, []);

  const speak = useCallback(
    async (text: string) => {
      if (!enabled) return;
      setMicState('speaking');
      await ttsSpeak(text, {
        onEnd: () => {
          // Only fall back to idle if nothing newer took over meanwhile.
          if (micStateRef.current === 'speaking') setMicState('idle');
        },
      });
    },
    [enabled, setMicState],
  );

  const api = useMemo<VoiceApi>(
    () => ({ enabled, setEnabled, micState, setMicState, speak }),
    [enabled, setEnabled, micState, setMicState, speak],
  );

  return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}

/** Access the voice API. Returns a no-op stub if used outside the provider so
 *  components never crash (e.g. in isolated tests). */
export function useVoice(): VoiceApi {
  const v = useContext(Ctx);
  if (v) return v;
  return {
    enabled: false,
    setEnabled: () => {},
    micState: 'idle',
    setMicState: () => {},
    speak: async () => {},
  };
}
